import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useDashboard } from '../../context/DashboardContext';
import { dashboardService } from '../../services/dashboardService';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';

interface QuickAddModalProps {
  isOpen: boolean;
  onClose: () => void;
  todayTotal: number;
}

const QuickAddModal: React.FC<QuickAddModalProps> = ({ isOpen, onClose, todayTotal }) => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const { refreshDashboard } = useDashboard();
  const { showToast } = useToast();
  const { t } = useTranslation();

  const [projectId, setProjectId] = useState<string>('');
  const [hours, setHours] = useState<string>('1');
  const [description, setDescription] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectId || !hours || !user) return;

    setLoading(true);
    try {
      await dashboardService.createEntry({
        project_id: parseInt(projectId),
        hours_worked: parseFloat(hours),
        entry_date: new Date().toISOString().split('T')[0],
        description: description || undefined,
      });

      showToast('Time entry added', 'success');
      await refreshDashboard();
      onClose();
      setProjectId('');
      setHours('1');
      setDescription('');
    } catch (error) {
      showToast('Failed to add time entry', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const bgColor = isDark ? 'bg-slate-900' : 'bg-white';
  const borderColor = isDark ? 'border-slate-700' : 'border-slate-200';
  const textColor = isDark ? 'text-white' : 'text-slate-900';
  const inputBg = isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-300 text-slate-900';

  const remainingHours = Math.max(0, 8 - todayTotal);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className={`${bgColor} rounded-lg shadow-xl max-w-md w-full mx-4 border ${borderColor}`}>
        <div className="p-6">
          <h2 className={`text-xl font-bold mb-4 ${textColor}`}>{t('dashboard.addTimeEntry')}</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${textColor}`}>
                {t('dashboard.project')}
              </label>
              <select
                value={projectId}
                onChange={e => setProjectId(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg ${inputBg} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                required
              >
                <option value="">Select project...</option>
                <option value="1">Backend API</option>
                <option value="2">Frontend</option>
                <option value="3">Documentation</option>
              </select>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${textColor}`}>
                {t('dashboard.hours')}
              </label>
              <input
                type="number"
                step="0.25"
                min="0.25"
                max={remainingHours + 5}
                value={hours}
                onChange={e => setHours(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg ${inputBg} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                required
              />
              <div className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                {remainingHours.toFixed(1)}h remaining today
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${textColor}`}>
                {t('dashboard.description')} (optional)
              </label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={3}
                className={`w-full px-3 py-2 border rounded-lg ${inputBg} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="What did you work on?"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className={`flex-1 py-2 px-4 rounded-lg border ${borderColor} ${textColor} font-medium hover:opacity-75 transition-opacity`}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={loading || !projectId || !hours}
                className="flex-1 py-2 px-4 rounded-lg bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white font-medium transition-colors"
              >
                {loading ? t('common.saving') : t('dashboard.addEntry')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default QuickAddModal;
